import { useState, useCallback, useEffect } from 'react';
import axios from '../../../_services/axiositeachWrapper';
import { Portlet } from '../../../_services/PortletService';
import PortletService from '../../../_services/PortletService';
import Config from '../../../_config/config';
import { Workspace, WorkspaceConfig } from './types';
import { buildDefaultWorkspaces } from './workspaceDefaults';

const STORAGE_KEY_PREFIX = 'iteach-workspaces-';
const PREFERENCE_NAME = 'workspace-config';
const CURRENT_VERSION = 1;

function getStorageKey(userId: number): string {
  return `${STORAGE_KEY_PREFIX}${Config.site}-${userId}`;
}

function loadWorkspaceConfigFromLocal(userId: number): WorkspaceConfig | null {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WorkspaceConfig;
    if (parsed.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveWorkspaceConfigToLocal(userId: number, config: WorkspaceConfig): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(config));
}

async function loadWorkspaceConfigFromServer(userId: number): Promise<WorkspaceConfig | null> {
  try {
    const response = await axios.get<{ success: boolean; data: WorkspaceConfig | null }>(
      `${Config.nodeSiteUrl}v1/preferences?userId=${userId}&name=${PREFERENCE_NAME}`
    );
    if (response.data?.success && response.data.data) {
      const config = response.data.data;
      if (config.version !== CURRENT_VERSION) return null;
      return config;
    }
    return null;
  } catch {
    return null;
  }
}

function saveWorkspaceConfigToServer(userId: number, config: WorkspaceConfig): void {
  axios.put(`${Config.nodeSiteUrl}v1/preferences`, {
    userId,
    name: PREFERENCE_NAME,
    data: config,
  }).catch((err) => {
    console.error('Failed to save workspace config to server:', err);
  });
}

export function useWorkspaces(portlets: Portlet[], userId: number) {
  const [config, setConfig] = useState<WorkspaceConfig>(() => {
    // Start with local cache or defaults; server load happens in useEffect
    const saved = loadWorkspaceConfigFromLocal(userId);
    if (saved) return saved;
    return buildDefaultWorkspaces(portlets);
  });

  const [serverLoaded, setServerLoaded] = useState(false);

  // Load from server on mount
  useEffect(() => {
    let cancelled = false;

    loadWorkspaceConfigFromServer(userId).then((serverConfig) => {
      if (cancelled) return;
      setServerLoaded(true);

      if (serverConfig) {
        // DEFENSIVE: Only update if we have portlets loaded to validate against
        if (portlets.length > 0) {
          // Validate portlet IDs from server config
          const validIds = new Set(portlets.map((p) => p.id));
          const cleanedWorkspaces = serverConfig.workspaces.map((ws) => {
            if (ws.isAllApps) return ws;
            // Filter out invalid portlet IDs that don't exist in user's actual portlets
            return {
              ...ws,
              portletIds: ws.portletIds.filter((id) => validIds.has(id)),
            };
          });

          const cleanedConfig = { ...serverConfig, workspaces: cleanedWorkspaces };
          setConfig(cleanedConfig);
          saveWorkspaceConfigToLocal(userId, cleanedConfig);

          console.log('[Workspace] Loaded config from server and validated portlet IDs');
        } else {
          // Portlets not loaded yet, save raw config
          setConfig(serverConfig);
          saveWorkspaceConfigToLocal(userId, serverConfig);
        }
      }
    });

    return () => { cancelled = true; };
  }, [userId, portlets]);

  // Re-initialize defaults when portlets load for the first time (initially empty)
  useEffect(() => {
    if (portlets.length === 0) return;

    setConfig((prev) => {
      // If we only have the All Apps workspace with no portletIds, it means
      // defaults were built with an empty portlets array - rebuild
      const nonAllApps = prev.workspaces.filter((w) => !w.isAllApps);
      if (nonAllApps.length === 0) {
        const fresh = buildDefaultWorkspaces(portlets);
        saveWorkspaceConfigToLocal(userId, fresh);
        saveWorkspaceConfigToServer(userId, fresh);
        return fresh;
      }

      // Clean up stale portlet IDs
      const validIds = new Set(portlets.map((p) => p.id));
      const updatedWorkspaces = prev.workspaces.map((ws) => {
        if (ws.isAllApps) return ws;
        return {
          ...ws,
          portletIds: ws.portletIds.filter((id) => validIds.has(id)),
        };
      });

      const updated = { ...prev, workspaces: updatedWorkspaces };
      saveWorkspaceConfigToLocal(userId, updated);
      return updated;
    });
  }, [portlets, userId]);

  const persist = useCallback(
    (newConfig: WorkspaceConfig) => {
      setConfig(newConfig);
      saveWorkspaceConfigToLocal(userId, newConfig);
      saveWorkspaceConfigToServer(userId, newConfig);
    },
    [userId]
  );

  const activeWorkspace =
    config.workspaces.find((w) => w.id === config.activeWorkspaceId) ||
    config.workspaces[0];

  const setActiveWorkspace = useCallback(
    (id: string) => {
      persist({ ...config, activeWorkspaceId: id });
    },
    [config, persist]
  );

  const addWorkspace = useCallback(
    (name: string, icon: string) => {
      const newWs: Workspace = {
        id: 'ws-custom-' + Date.now(),
        name,
        icon,
        portletIds: [],
        isDefault: false,
        isAllApps: false,
        order: config.workspaces.length,
      };
      persist({
        ...config,
        workspaces: [...config.workspaces, newWs],
        activeWorkspaceId: newWs.id,
      });
    },
    [config, persist]
  );

  const deleteWorkspace = useCallback(
    (id: string) => {
      const ws = config.workspaces.find((w) => w.id === id);
      if (!ws || ws.isAllApps || ws.isDefault) return;

      const filtered = config.workspaces.filter((w) => w.id !== id);
      const newActiveId =
        config.activeWorkspaceId === id ? 'ws-all-apps' : config.activeWorkspaceId;

      persist({
        ...config,
        workspaces: filtered,
        activeWorkspaceId: newActiveId,
      });
    },
    [config, persist]
  );

  const renameWorkspace = useCallback(
    (id: string, newName: string) => {
      const ws = config.workspaces.find((w) => w.id === id);
      if (!ws || ws.isAllApps) return;

      persist({
        ...config,
        workspaces: config.workspaces.map((w) =>
          w.id === id ? { ...w, name: newName } : w
        ),
      });
    },
    [config, persist]
  );

  const addPortletToWorkspace = useCallback(
    (workspaceId: string, portletId: string) => {
      persist({
        ...config,
        workspaces: config.workspaces.map((w) => {
          if (w.id !== workspaceId || w.isAllApps) return w;
          if (w.portletIds.includes(portletId)) return w;
          return { ...w, portletIds: [...w.portletIds, portletId] };
        }),
      });
    },
    [config, persist]
  );

  const removePortletFromWorkspace = useCallback(
    (workspaceId: string, portletId: string) => {
      persist({
        ...config,
        workspaces: config.workspaces.map((w) => {
          if (w.id !== workspaceId || w.isAllApps) return w;
          return {
            ...w,
            portletIds: w.portletIds.filter((id) => id !== portletId),
          };
        }),
      });
    },
    [config, persist]
  );

  const togglePortletInWorkspace = useCallback(
    (workspaceId: string, portletId: string) => {
      const ws = config.workspaces.find((w) => w.id === workspaceId);
      if (!ws || ws.isAllApps) return;

      if (ws.portletIds.includes(portletId)) {
        removePortletFromWorkspace(workspaceId, portletId);
      } else {
        addPortletToWorkspace(workspaceId, portletId);
      }
    },
    [config, addPortletToWorkspace, removePortletFromWorkspace]
  );

  const getPortletWorkspaces = useCallback(
    (portletId: string): string[] => {
      return config.workspaces
        .filter((w) => !w.isAllApps && w.portletIds.includes(portletId))
        .map((w) => w.id);
    },
    [config]
  );

  return {
    workspaces: [...config.workspaces].sort((a, b) => a.order - b.order),
    activeWorkspace,
    setActiveWorkspace,
    addWorkspace,
    deleteWorkspace,
    renameWorkspace,
    addPortletToWorkspace,
    removePortletFromWorkspace,
    togglePortletInWorkspace,
    getPortletWorkspaces,
  };
}
